import Vue from 'vue';
import AgentSettingsService from '@/services/account/agentSettingsService';

const state = () => ({
    agentSettings: JSON.parse(localStorage.getItem('agentSettings')) || {
        uuid: null,
        name: null,
        conversation_tone: null,
        account_id: null,
        enabled: false,
        skills: [
            {
                account_id: '',
                skill_id: '',
                enabled: false,
                internal_name: '',
                config_values: {},
                skill_details: [],
                date_created: 0,
                date_updated: 0,
            },
        ],
        date_created: 0,
        isUpdatingGlobal: false,
    },
    isUpdatingGlobal: false, // Ensure it exists on root state if accessed directly, though nested in agentSettings usually
});

const getters = {
    agentSettings: (state) => state.agentSettings,
    getAgentName: (state) => state.agentSettings.name || 'Agent',

    /**
     * Idioma por defecto de la cuenta con su variante regional (p.ej. "es-ES").
     * null si aún no se ha cargado: quien lo consuma debe tratar el desconocido
     * como "no inglés" y dejar decidir al backend, en vez de asumir.
     */
    getAccountLanguage: (state) => state.agentSettings.language || null,
    isUpdatingGlobal: (state) => state.isUpdatingGlobal,
    getSkillByInternalName: (state) => (internal_name) => {
        if (state.agentSettings && state.agentSettings.skills) {
            return (
                state.agentSettings.skills.find(
                    (skill) => skill.skill_details?.[0]?.internal_name.toLowerCase() === internal_name.toLowerCase(),
                ) || null
            );
        }
        return null;
    },
    getSkillByPlatform: (state) => (platform) => {
        if (state.agentSettings && state.agentSettings.skills) {
            return (
                state.agentSettings.skills.find((skill) => skill.platform.toLowerCase() === platform.toLowerCase()) ||
                null
            );
        }
        return null;
    },
    hasAnyCommunicationChannel: (state) => {
        return Array.isArray(state.agentSettings?.skills)
            ? state.agentSettings.skills.some((skill) => skill.skill_details?.[0]?.type === 'CHANNEL')
            : false;
    },
    hasAnyMainApp: (state) => {
        return Array.isArray(state.agentSettings?.skills)
            ? state.agentSettings.skills.some((skill) => skill.skill_details?.[0]?.type === 'MAIN_APP')
            : false;
    },

    isActiveAccount: (state, getters) => {
        return getters.hasAnyCommunicationChannel || getters.hasAnyMainApp;
    },
    getConnectedChannels: (state) => {
        const skills = Array.isArray(state.agentSettings?.skills) ? state.agentSettings.skills : [];
        return skills
            .filter((skill) => skill.skill_details?.[0]?.type === 'CHANNEL')
            .map((skill) => skill.skill_details?.[0]);
    },
    getChannelSkills: (state) => {
        if (state.agentSettings && state.agentSettings.skills) {
            return state.agentSettings.skills
                .filter((skill) => skill.skill_details?.[0]?.type === 'CHANNEL')
                .sort((a, b) => {
                    const nameA = a.skill_details?.[0]?.name || a.skill_details?.[0]?.internal_name || '';
                    const nameB = b.skill_details?.[0]?.name || b.skill_details?.[0]?.internal_name || '';
                    return nameA.localeCompare(nameB);
                });
        }
        return [];
    },
    isAgentSettingsEnabled: (state) => state.agentSettings.enabled,
    getSkills: (state) => state.agentSettings.skills || [],
    hasShopifySkill: (state) => {
        const shopifySkillId = 'd74f8439-bda9-420a-8b20-cbb80e5f7510';
        return Array.isArray(state.agentSettings?.skills)
            ? state.agentSettings.skills.some((skill) => skill.skill_id === shopifySkillId)
            : false;
    },
    hasStartConversationSkill: (state) => {
        const startConversationSkillId = '21e4c0a2-8aac-4703-908d-356def6b35bc';
        return Array.isArray(state.agentSettings?.skills)
            ? state.agentSettings.skills.some(
                  (skill) => skill.skill_id === startConversationSkillId && skill.enabled !== false,
              )
            : false;
    },
    hasTranslateSkill: (state) => {
        const translateSkillId = '2a80f0c0-6d24-4f9e-bd42-f8b7489cdc33';
        return Array.isArray(state.agentSettings?.skills)
            ? state.agentSettings.skills.some((skill) => skill.skill_id === translateSkillId)
            : false;
    },
    hasEcommerceSkill: (state) => {
        const ecommerceSkillIds = [
            'd74f8439-bda9-420a-8b20-cbb80e5f7510', // Shopify
            '124f8439-bca9-420a-8f20-c1b80e5f7512', // PrestaShop
            '742de54f-6ed3-4357-86dd-5be8e329c0f8', // Magento
            'ab7aa0d4-9f70-4144-af1e-a926232b4871', // WooCommerce
            '7d2f4c8a-91b3-4e6d-a5f0-3c8e2b7d9a14', // Tiendanube
        ];
        return Array.isArray(state.agentSettings?.skills)
            ? state.agentSettings.skills.some((skill) => ecommerceSkillIds.includes(skill.skill_id))
            : false;
    },
    // Mismo criterio que Account#getMainAppsWithCatalog en el backend: cualquier
    // main app cuyo catálogo se puede importar (e-commerce, TurnoChic, ...).
    hasCatalogMainApp: (state) => {
        return Array.isArray(state.agentSettings?.skills)
            ? state.agentSettings.skills.some(
                  (skill) =>
                      skill.skill_details?.[0]?.type === 'MAIN_APP' && skill.skill_details?.[0]?.require_catalog,
              )
            : false;
    },
};

const actions = {
    clearAgentSettingsState({ commit }) {
        localStorage.removeItem('agentSettings');
        commit('CLEAR_AGENT_SETTINGS');
    },

    async updateAgentSetting({ commit, state }) {
        try {
            const newAgentSettings = await AgentSettingsService.getAgentSettings();
            commit('SET_AGENT_SETTINGS', newAgentSettings.data);
            localStorage.setItem('agentSettings', JSON.stringify(state.agentSettings));
        } catch (error) {
            console.error('Error updating agent settings:', error);
        }
    },

    async updateAgentSettings({ dispatch }, agentSettingsPayload) {
        try {
            await AgentSettingsService.editAgentSettings(agentSettingsPayload);
            await dispatch('updateAgentSetting');
        } catch (error) {
            console.error('Error updating agent settings:', error);
            throw error;
        }
    },

    async switchAgentSettings({ commit, state }, newEnabledState) {
        if (newEnabledState === state.agentSettings.enabled) {
            return; // No change needed
        }

        const previousState = state.agentSettings.enabled;
        commit('SWITCH_AGENT_SETTINGS', newEnabledState);

        try {
            await (newEnabledState
                ? AgentSettingsService.enableAgentSettings()
                : AgentSettingsService.disableAgentSettings());
        } catch (error) {
            commit('SWITCH_AGENT_SETTINGS', previousState); // Rollback to previous state
            throw error; // Re-throw to allow caller to handle if needed
        }
    },

    async toggleSkill({ state, commit }, { skillId, isActive }) {
        const skill = state.agentSettings.skills.find((s) => s.skill_id === skillId);
        if (!skill) return;

        const previousState = skill.ai_active ?? true;
        commit('SET_SKILL_AI_ACTIVE', { skillId, ai_active: isActive });

        try {
            // Send null config_values to avoid overwriting existing backend config.
            // The backend merges config_values, so null means "keep existing values".
            await AgentSettingsService.modifyAgentSettings(null, skillId, isActive);
        } catch (error) {
            console.error('Failed to toggle skill:', error);
            commit('SET_SKILL_AI_ACTIVE', { skillId, ai_active: previousState });
            throw error;
        }
    },

    async subscribeToAgentSettingsUpdates({ state, dispatch }) {
        const accountId = state.agentSettings.account_id;
        const topic = `/account/${accountId}/agent/${accountId}`;
        try {
            await dispatch(
                'websocket/subscribe',
                {
                    topic,
                    callback: (data) => {
                        dispatch('handleAgentSettingsUpdate', data);
                    },
                },
                { root: true },
            );
        } catch (error) {
            console.error(`Failed to subscribe to ${topic}:`, error);
        }
    },

    unsubscribeFromAgentSettingsUpdates({ state, dispatch }) {
        const accountId = state.agentSettings.account_id;
        const topic = `/account/${accountId}/agent/${accountId}`;
        dispatch('websocket/unsubscribe', { topic }, { root: true });
    },

    handleAgentSettingsUpdate({ state, commit, dispatch }, event) {
        if (!event || !event.data) return;

        // Prevent race conditions/bouncing by ignoring server events during manual global update
        if (state.isUpdatingGlobal) return;

        try {
            switch (event.name) {
                case 'AgentSettingsDisabled':
                    console.log('AgentSettingsDisabled');
                    if (state.agentSettings.enabled === false) {
                        return; // No change needed
                    }
                    commit('SWITCH_AGENT_SETTINGS', false);
                    break;
                case 'AgentSettingsEnabled':
                    console.log('AgentSettingsEnabled');
                    if (state.agentSettings.enabled === true) {
                        return; // No change needed
                    }
                    commit('SWITCH_AGENT_SETTINGS', true);
                    break;
                case 'AgentSettingsEnablingError':
                    console.log('AgentSettingsEnablingError');
                    if (state.agentSettings.enabled === false) {
                        return; // No change needed
                    }
                    commit('SWITCH_AGENT_SETTINGS', false);
                    break;
                case 'SkillAdded':
                case 'SkillDeleted':
                    // Note: 'SkillUpdated' is commented out to prevent list instability/re-fetching during toggles
                    if (state.isUpdatingGlobal) return;
                    dispatch('updateAgentSetting');
                    break;
                default:
                    console.warn(`Unhandled agent settings event: ${event.name}`);
            }
        } catch (error) {
            console.error('Error parsing agent settings update data:', error);
        }
    },

    updateSkillConfigValues({ commit }, { skillId, configValues }) {
        commit('UPDATE_SKILL_CONFIG_VALUES', { skillId, configValues });
    },
};

const mutations = {
    SET_AGENT_SETTINGS(state, agentSettings) {
        if (agentSettings.skills) {
            agentSettings.skills = agentSettings.skills.map((skill) => {
                return {
                    ...skill,
                    ai_active: skill.ai_active ?? true,
                };
            });
        }
        state.agentSettings = { ...agentSettings };
    },
    SWITCH_AGENT_SETTINGS(state, isEnabled) {
        state.agentSettings.enabled = isEnabled;
    },
    CLEAR_AGENT_SETTINGS(state) {
        state.agentSettings = {
            uuid: null,
            name: null,
            conversation_tone: null,
            account_id: null,
            enabled: false,
            skills: [
                {
                    skill_id: null,
                    enabled: null,
                    internal_name: null,
                    internal_prompt: null,
                    skill_details: [],
                    date_created: 0,
                    date_updated: 0,
                    ai_active: true,
                },
            ],
            date_created: 0,
        };
    },
    UPDATE_SKILL_CONFIG_VALUES(state, { skillId, configValues }) {
        const skill = state.agentSettings.skills.find((skill) => {
            const match = skill.skill_id === skillId;
            return match;
        });

        if (skill) {
            skill.config_values = configValues;
        } else {
            console.error('[AgentSettings] Skill not found with ID:', skillId);
        }
    },
    SET_SKILL_AI_ACTIVE(state, { skillId, ai_active }) {
        const skill = state.agentSettings.skills.find((s) => s.skill_id === skillId);
        if (skill) {
            // Use Vue.set to ensure reactivity for potentially new property
            Vue.set(skill, 'ai_active', ai_active);
        }
    },
    SET_IS_UPDATING_GLOBAL(state, isUpdating) {
        state.isUpdatingGlobal = isUpdating;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
